import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    ChevronRight,
    Minus,
    Plus,
    ShoppingCart,
    Check,
    Package,
    Truck,
    Shield,
    ArrowLeft,
    CreditCard
} from 'lucide-react';
import PageLayout from '../../components/Layout/PageLayout';
import RFQButton from '../../components/rfq/RFQButton';
import { getCategoryGroup, categoryPath } from '../../data/productCategories';
import BrandPlaceholder from '../../components/product/BrandPlaceholder';
import { useCart } from '../../context/CartContext';
import { getProductById } from '../../api/ingram/catalogService';
import './ProductPage.scss';

const ProductPage = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { addItem, isInCart, getItemQuantity } = useCart();

    // State
    const [product, setProduct] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [activeTab, setActiveTab] = useState('specifications');
    const [addedToCart, setAddedToCart] = useState(false);
    // Real manufacturer image URL, fetched from our image endpoint after the
    // base product loads. Null until resolved — page renders BrandPlaceholder
    // in the meantime, no ugly SVG or `via.placeholder.com` fallback.
    const [imageUrl, setImageUrl] = useState(null);
    const [imgError, setImgError] = useState(false);

    // Fetch product data
    useEffect(() => {
        // React Router v6 preserves scroll from the previous page; on a
        // catalog page that's typically scrolled far down, the product
        // detail would land the user at the footer. Force top on mount.
        window.scrollTo(0, 0);

        const fetchProduct = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const data = await getProductById(productId);
                setProduct(data);
            } catch (err) {
                console.error('Failed to fetch product:', err);
                setError(err.message || 'Failed to load product details');
            } finally {
                setIsLoading(false);
            }
        };

        if (productId) {
            fetchProduct();
        }
    }, [productId]);

    // If the /search or /product detail response already contained a cached
    // imageUrl, use it immediately. Otherwise eagerly fetch through the
    // scraper cascade (product detail = definite intent to view).
    useEffect(() => {
        if (!product?.partNumber) return;
        if (product.imageUrl) { setImageUrl(product.imageUrl); return; }
        const apiBase = process.env.REACT_APP_API_URL || '/api';
        const params = new URLSearchParams();
        if (product.manufacturer) params.set('brand', product.manufacturer);
        if (product.upc) params.set('upc', product.upc);
        if (product.name) params.set('name', product.name);
        const ingramPn = product.ingramPartNumber || product.id;
        if (ingramPn) params.set('ingramPartNumber', ingramPn);
        const url = `${apiBase}/products/image/${encodeURIComponent(product.partNumber)}?${params.toString()}`;
        let cancelled = false;
        fetch(url)
            .then((r) => (r.ok ? r.json() : { imageUrl: null }))
            .then((res) => {
                if (!cancelled && res?.imageUrl) setImageUrl(res.imageUrl);
            })
            .catch(() => { /* fall back to placeholder */ });
        return () => { cancelled = true; };
    }, [product?.partNumber, product?.manufacturer, product?.name, product?.imageUrl]);

    // Handle quantity changes
    const decreaseQuantity = () => {
        if (quantity > 1) {
            setQuantity(q => q - 1);
        }
    };

    const increaseQuantity = () => {
        setQuantity(q => q + 1);
    };

    // Handle add to cart
    const handleAddToCart = () => {
        if (product && product.inStock) {
            addItem(product, quantity);
            setAddedToCart(true);
            setTimeout(() => setAddedToCart(false), 2000);
        }
    };

    // Buy Now — add to cart and go straight to checkout. Only offered for
    // in-stock items that have a real price (priceNumeric > 0) and only when
    // Stripe checkout is configured in this build.
    const stripeEnabled = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    const canBuyNow = stripeEnabled && product && product.inStock && Number(product.priceNumeric) > 0;
    const handleBuyNow = () => {
        if (!canBuyNow) return;
        addItem(product, quantity);
        navigate('/checkout');
    };

    // Loading state
    if (isLoading) {
        return (
            <PageLayout solidHeader>
                <div className="jk-product-page__loading">
                    <div className="jk-product-page__loading-spinner">
                        <div className="jk-product-page__loading-circle"></div>
                    </div>
                    <p>Loading product details...</p>
                </div>
            </PageLayout>
        );
    }

    // Error state
    if (error) {
        return (
            <PageLayout solidHeader>
                <div className="jk-product-page__error">
                    <Package size={48} />
                    <h2>Product Not Found</h2>
                    <p>{error}</p>
                    <button className="jk-btn jk-btn--primary" onClick={() => navigate('/accessories')}>
                        <ArrowLeft size={18} />
                        Back to Products
                    </button>
                </div>
            </PageLayout>
        );
    }

    if (!product) {
        return null;
    }

    const inCart = isInCart(product.id);
    const cartQuantity = getItemQuantity(product.id);

    // Honest discount % — only shown when MSRP is genuinely higher than our
    // customer price. Where our 12% markup pushes price above Ingram's
    // retailPrice we treat that as "no discount" (rather than a misleading
    // strikethrough).
    const priceNum = Number(product.priceNumeric) || 0;
    const msrpNum = product.msrp
        ? Number(String(product.msrp).replace(/[^\d.]/g, ''))
        : 0;
    const discountPct = msrpNum > priceNum && priceNum > 0
        ? Math.round(((msrpNum - priceNum) / msrpNum) * 100)
        : 0;

    const hasRealImage = Boolean(imageUrl) && !imgError;

    // Keep the hovered spot under the cursor while the image is scaled up.
    const handleImageMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        e.currentTarget.style.setProperty('--zoom-x', `${x}%`);
        e.currentTarget.style.setProperty('--zoom-y', `${y}%`);
    };

    return (
        <PageLayout solidHeader>
            <div className="jk-product-page">
                <div className="jk-product-page__container">
                    {/* Breadcrumb: Home > Products > [Group] > [Category] > name */}
                    <nav className="jk-product-page__breadcrumb">
                        <Link to="/">Home</Link>
                        <ChevronRight size={16} />
                        <Link to="/accessories">Products</Link>
                        {getCategoryGroup(product.category) && (
                            <>
                                <ChevronRight size={16} />
                                <span>{getCategoryGroup(product.category)}</span>
                            </>
                        )}
                        {product.category && product.category !== 'General' && (
                            <>
                                <ChevronRight size={16} />
                                <Link to={categoryPath(product.category)}>{product.category}</Link>
                            </>
                        )}
                        <ChevronRight size={16} />
                        <span>{product.name}</span>
                    </nav>

                    <div className="jk-product-page__content">
                        {/* Product Image */}
                        <div className="jk-product-page__gallery">
                            <div
                                className={`jk-product-page__image${hasRealImage ? ' jk-product-page__image--zoomable' : ''}`}
                                onMouseMove={hasRealImage ? handleImageMouseMove : undefined}
                            >
                                {(!imageUrl || imgError) ? (
                                    <BrandPlaceholder
                                        brand={product.manufacturer}
                                        name={product.name}
                                        category={product.category}
                                        size="hero"
                                    />
                                ) : (
                                    <img
                                        src={imageUrl}
                                        alt={product.name}
                                        onError={() => setImgError(true)}
                                    />
                                )}
                                {!product.inStock && (
                                    <span className="jk-product-page__badge jk-product-page__badge--out">
                                        Out of Stock
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Product Details */}
                        <div className="jk-product-page__details">
                            {/* Manufacturer label removed — it's already in
                                the Specifications table below and was
                                visually redundant above the title. */}
                            <h1 className="jk-product-page__title">{product.name}</h1>

                            <div className="jk-product-page__meta">
                                <span className="jk-product-page__part">
                                    Part #: {product.partNumber}
                                </span>
                                {product.upc && (
                                    <span className="jk-product-page__upc">
                                        UPC: {product.upc}
                                    </span>
                                )}
                            </div>

                            <div className="jk-product-page__price-section">
                                <span className="jk-product-page__price">{product.price}</span>
                                {discountPct > 0 && (
                                    <>
                                        <span className="jk-product-page__msrp">MSRP: {product.msrp}</span>
                                        <span className="jk-product-page__discount">-{discountPct}% OFF</span>
                                    </>
                                )}
                            </div>

                            <div className={`jk-product-page__stock ${product.inStock ? 'jk-product-page__stock--available' : 'jk-product-page__stock--out'}`}>
                                <span className="jk-product-page__stock-dot"></span>
                                {product.inStock ? (
                                    <>
                                        In Stock
                                        {product.availableQuantity > 0 && (
                                            <span className="jk-product-page__stock-qty">
                                                ({product.availableQuantity} available)
                                            </span>
                                        )}
                                    </>
                                ) : (
                                    product.stockStatus || 'Out of Stock'
                                )}
                            </div>

                            {/* Quantity Selector */}
                            <div className="jk-product-page__quantity">
                                <label>Quantity:</label>
                                <div className="jk-product-page__quantity-controls">
                                    <button
                                        onClick={decreaseQuantity}
                                        disabled={quantity <= 1}
                                        aria-label="Decrease quantity"
                                    >
                                        <Minus size={18} />
                                    </button>
                                    <span>{quantity}</span>
                                    <button
                                        onClick={increaseQuantity}
                                        aria-label="Increase quantity"
                                    >
                                        <Plus size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="jk-product-page__actions">
                                {canBuyNow && (
                                    <button
                                        className="jk-btn jk-btn--primary jk-product-page__buynow-btn"
                                        onClick={handleBuyNow}
                                    >
                                        <CreditCard size={20} />
                                        Buy Now
                                    </button>
                                )}
                                <button
                                    className={`jk-btn jk-btn--primary jk-product-page__cart-btn ${addedToCart ? 'jk-product-page__cart-btn--added' : ''}`}
                                    onClick={handleAddToCart}
                                    disabled={!product.inStock}
                                >
                                    {addedToCart ? (
                                        <>
                                            <Check size={20} />
                                            Added to Cart
                                        </>
                                    ) : (
                                        <>
                                            <ShoppingCart size={20} />
                                            Add to Cart
                                        </>
                                    )}
                                </button>

                                <RFQButton product={product} quantity={quantity} />
                            </div>

                            {inCart && (
                                <p className="jk-product-page__cart-info">
                                    You have {cartQuantity} of this item in your cart.{' '}
                                    <Link to="/cart">View Cart</Link>
                                </p>
                            )}

                            {/* Features */}
                            <div className="jk-product-page__features">
                                <div className="jk-product-page__feature">
                                    <Truck size={20} />
                                    <span>Fast Shipping Available</span>
                                </div>
                                <div className="jk-product-page__feature">
                                    <Shield size={20} />
                                    <span>Manufacturer Warranty</span>
                                </div>
                                <div className="jk-product-page__feature">
                                    <Package size={20} />
                                    <span>Authorized Reseller</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs Section */}
                    <div className="jk-product-page__tabs">
                        <div className="jk-product-page__tabs-header">
                            <button
                                className={`jk-product-page__tab ${activeTab === 'specifications' ? 'active' : ''}`}
                                onClick={() => setActiveTab('specifications')}
                            >
                                Specifications
                            </button>
                            <button
                                className={`jk-product-page__tab ${activeTab === 'description' ? 'active' : ''}`}
                                onClick={() => setActiveTab('description')}
                            >
                                Description
                            </button>
                        </div>

                        <div className="jk-product-page__tabs-content">
                            {activeTab === 'specifications' && (
                                <div className="jk-product-page__specs">
                                    <table>
                                        <tbody>
                                            <tr>
                                                <th>Manufacturer</th>
                                                <td>{product.manufacturer}</td>
                                            </tr>
                                            <tr>
                                                <th>Part Number</th>
                                                <td>{product.partNumber}</td>
                                            </tr>
                                            <tr>
                                                <th>Category</th>
                                                <td>{product.category}</td>
                                            </tr>
                                            {product.subCategory && (
                                                <tr>
                                                    <th>Sub-Category</th>
                                                    <td>{product.subCategory}</td>
                                                </tr>
                                            )}
                                            {product.upc && (
                                                <tr>
                                                    <th>UPC</th>
                                                    <td>{product.upc}</td>
                                                </tr>
                                            )}
                                            <tr>
                                                <th>Product Type</th>
                                                <td>{product.productType === 'subscription' ? 'Software/Subscription' : 'Physical Product'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {activeTab === 'description' && (
                                <div className="jk-product-page__description">
                                    {product.description ? (
                                        <p>{product.description}</p>
                                    ) : (
                                        <p className="jk-product-page__no-description">
                                            No detailed description available for this product. Please contact us for more information.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default ProductPage;
